import { useState, useMemo, useEffect } from 'react';
import { Button, CustomSelect, DateTimePicker } from '@/components';
import { useQuery } from '@tanstack/react-query';
import { getWhatsappTemplates } from '../api';
import { getGmailTemplates } from '../../api';
import { generateTemplateSchema } from '../../utils/templateUtils';
import { INTERVAL_UNITS } from '../../utils/intervalUtils';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { TemplatePhonePreview } from '../../components/TemplatePhonePreview';
import { VariableValueInput } from '../../components/VariableValueInput';
import {
  DYNAMIC_CONTACT_FIELDS,
  contactFieldToken,
} from '../../utils/dynamicContactVars';
import { EmailTemplatePreview } from '../../components/EmailTemplatePreview';

import { getContactChannelCapabilities } from '../utils/contactCapabilities';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';
import { ChannelAvailabilityState } from '../../components/ChannelAvailabilityState';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';

export function CRMFollowUpForm({
  companyId,
  contact,
  onSubmit,
  isPending,
  onCancel,
  initialChannel,
  variant = 'page',
  submitDisabled = false,
}) {
  const { hasEmail, hasPhone } = useMemo(
    () => getContactChannelCapabilities(contact),
    [contact],
  );
  const integration = useMessagingChannels(companyId);
  const { can } = usePermissions();
  const channelOptions = useMemo(() => {
    if (!integration.isReady) return [];
    const allows = (value) => {
      if (hasEmail && hasPhone) return true;
      if (!hasEmail && !hasPhone) return true;
      return value === 'email' ? hasEmail : hasPhone;
    };
    const options = [];
    if (integration.email && allows('email')) {
      options.push({ value: 'email', label: 'Email (Gmail)' });
    }
    if (integration.whatsapp && allows('whatsapp')) {
      options.push({ value: 'whatsapp', label: 'WhatsApp' });
    }
    return options;
  }, [
    integration.isReady,
    integration.email,
    integration.whatsapp,
    hasEmail,
    hasPhone,
  ]);

  const defaultChannel = useMemo(() => {
    if (initialChannel && (initialChannel === 'email' ? hasEmail : hasPhone)) {
      return initialChannel;
    }
    if (hasEmail && !hasPhone) return 'email';
    return 'whatsapp';
  }, [initialChannel, hasEmail, hasPhone]);

  const [channel, setChannel] = useState(defaultChannel);

  useEffect(() => {
    if (!channelOptions.length) return;
    if (!channelOptions.some((option) => option.value === channel)) {
      setChannel(channelOptions[0].value);
    }
  }, [channel, channelOptions]);
  const [followUpType, setFollowUpType] = useState('scheduled');
  const [messageType, setMessageType] = useState('message');

  // Dynamic user & contact details for preview
  const userInfo = useMemo(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) return JSON.parse(storedUser);
    } catch {
      // Ignore parse error
    }
    return {};
  }, []);

  const companyName = useMemo(() => {
    return (
      localStorage.getItem('company_name') ||
      userInfo.company_name ||
      userInfo.company?.name ||
      'flexion infotech'
    );
  }, [userInfo]);

  const senderName = useMemo(() => {
    const fullName = [userInfo.first_name, userInfo.last_name]
      .filter(Boolean)
      .join(' ');
    return fullName || userInfo.name || 'Ridham Talaviya';
  }, [userInfo]);

  const senderEmail = useMemo(() => {
    return (
      userInfo.email || userInfo.user_email || 'ridham@flexioninfotech.com'
    );
  }, [userInfo]);

  const contactEmail = useMemo(() => {
    return (
      contact?.email ||
      contact?.from_email ||
      contact?.recipient_email ||
      'customer@email.com'
    );
  }, [contact]);

  const contactName = useMemo(() => {
    return (
      contact?.name ||
      contact?.first_name ||
      contact?.client_name ||
      (contact?.email ? contact.email.split('@')[0] : 'Customer')
    );
  }, [contact]);

  // Template fields
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateValues, setTemplateValues] = useState({});
  const [mediaFile, setMediaFile] = useState(null);

  // WhatsApp Templates Query
  const {
    data: whatsappTemplatesResponse,
    isLoading: isWhatsappTemplatesLoading,
  } = useQuery({
    queryKey: ['whatsapp-templates', companyId],
    queryFn: () => getWhatsappTemplates(companyId),
    enabled:
      !!companyId && channel === 'whatsapp' && messageType === 'template',
  });
  const whatsappTemplates = useMemo(
    () => whatsappTemplatesResponse?.results || whatsappTemplatesResponse || [],
    [whatsappTemplatesResponse],
  );

  // Email Templates Query
  const { data: emailTemplatesResponse, isLoading: isEmailTemplatesLoading } =
    useQuery({
      queryKey: ['gmail-templates', companyId],
      queryFn: () => getGmailTemplates(companyId),
      enabled: !!companyId && channel === 'email' && messageType === 'template',
    });
  const emailTemplates = useMemo(
    () => emailTemplatesResponse?.results || emailTemplatesResponse || [],
    [emailTemplatesResponse],
  );

  const selectedTemplate = useMemo(() => {
    if (channel === 'whatsapp') {
      return whatsappTemplates.find(
        (t) => String(t.id || t.uuid) === String(selectedTemplateId),
      );
    }
    return emailTemplates.find(
      (t) => String(t.id) === String(selectedTemplateId),
    );
  }, [channel, selectedTemplateId, whatsappTemplates, emailTemplates]);

  const templateSchema = useMemo(() => {
    return channel === 'whatsapp' && selectedTemplate
      ? generateTemplateSchema(selectedTemplate)
      : null;
  }, [channel, selectedTemplate]);

  const defaultEmailBody = useMemo(() => {
    return `<p>Hi <strong>${contactName}</strong>,</p><p>I hope this email finds you well.<br>I'm reaching out to follow up on our recent conversation. Please let me know if you have any questions or need further information.</p><p>Looking forward to your response.</p><p>Best regards,<br><strong>${senderName}</strong></p>`;
  }, [contactName, senderName]);

  const defaultEmailSubject = 'Following up on our recent conversation';

  // Standard message field & email subject
  const [emailSubject, setEmailSubject] = useState('');
  const [standardMessageBody, setStandardMessageBody] = useState('');

  const previewEmailSubject = useMemo(() => {
    return emailSubject.trim() || defaultEmailSubject;
  }, [emailSubject]);

  const previewEmailBody = useMemo(() => {
    if (
      standardMessageBody &&
      standardMessageBody.replace(/<[^>]*>/g, '').trim()
    ) {
      return standardMessageBody;
    }
    return defaultEmailBody;
  }, [standardMessageBody, defaultEmailBody]);

  // Schedule fields
  const [scheduledDateTime, setScheduledDateTime] = useState(null);

  // Recurring fields
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState('days');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(0);
  const [stopOnReply, setStopOnReply] = useState(true);

  const handleSubmit = async () => {
    if (isPending) return;
    if (
      !integration.isReady ||
      !channelOptions.some((option) => option.value === channel)
    ) {
      return;
    }

    const payload = {
      channel,
      email_subject:
        channel === 'email' ? emailSubject.trim() || defaultEmailSubject : '',
      follow_up_type: followUpType,
      message_type: messageType,
      stop_on_reply: stopOnReply,
    };

    if (channel === 'whatsapp' && messageType === 'template') {
      const t = selectedTemplate || {};
      payload.template_id = String(selectedTemplateId);
      payload.template_name = t.name || '';
      payload.template_language = t.language || 'en_US';
      payload.template_variables =
        Object.keys(templateValues).length > 0
          ? JSON.stringify(templateValues)
          : '';
      payload.standard_message_body = '';

      if (mediaFile && templateSchema?.media_field) {
        payload.media_type = templateSchema.media_field.type;
        payload.document_filename = mediaFile.name;

        // Read file as base64
        const base64Data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(mediaFile);
        });
        payload.document_base64 = base64Data;
      }
    } else if (channel === 'email' && messageType === 'template') {
      const t = selectedTemplate || {};
      payload.template_id = String(selectedTemplateId);
      payload.template_name = t.name || t.subject || '';
      payload.standard_message_body = standardMessageBody || t.body || '';
      payload.email_subject = emailSubject || t.subject || '';
    } else {
      payload.standard_message_body =
        standardMessageBody.trim() ||
        (channel === 'email' ? defaultEmailBody : '');
      payload.template_id = '';
      payload.template_name = '';
      payload.template_language = '';
      payload.template_variables = '';
    }

    if (followUpType === 'scheduled') {
      if (scheduledDateTime) {
        payload.scheduled_date_time = scheduledDateTime.toISOString();
      }
      payload.interval_value = 0;
      payload.interval_unit = '';
      payload.start_date = scheduledDateTime
        ? scheduledDateTime.toISOString()
        : new Date().toISOString();
      payload.end_date = scheduledDateTime
        ? scheduledDateTime.toISOString()
        : new Date().toISOString();
      payload.max_attempts = 1;
    } else {
      payload.interval_value = Number(intervalValue);
      payload.interval_unit = intervalUnit;
      if (startDate) payload.start_date = startDate.toISOString();
      if (endDate) payload.end_date = endDate.toISOString();
      payload.max_attempts = Number(maxAttempts);
      payload.scheduled_date_time = startDate
        ? startDate.toISOString()
        : new Date().toISOString();
    }

    onSubmit(payload);
  };

  const scheduledDateTimeField = followUpType === 'scheduled' && (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
        Scheduled Date & Time <span className="text-red-500">*</span>
      </label>
      <DateTimePicker
        selected={scheduledDateTime}
        onChange={(date) => setScheduledDateTime(date)}
        mode="datetime"
        allowPastDates={false}
        disablePastTime={true}
        placeholderText="Select schedule date & time"
        disabled={isPending}
        className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
      />
    </div>
  );

  const isSubmitDisabled =
    isPending ||
    submitDisabled ||
    (channel === 'whatsapp' &&
      messageType === 'template' &&
      !selectedTemplateId) ||
    (channel === 'whatsapp' &&
      messageType === 'message' &&
      !standardMessageBody.trim()) ||
    (channel === 'email' &&
      messageType === 'template' &&
      !selectedTemplateId) ||
    (followUpType === 'scheduled' && !scheduledDateTime) ||
    (followUpType === 'interval' && !startDate);

  return (
    <div className="w-full flex flex-col gap-6 animate-fadeIn pb-4">
      {onCancel && variant !== 'drawer' && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center justify-center w-9 h-9 rounded-[8px] bg-white border border-primary text-primary hover:bg-surface-mint transition-colors focus:outline-none shrink-0"
            aria-label="Back"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.25}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-xl font-bold text-[#1F2241]">Add Follow-Up</h2>
        </div>
      )}

      <div
        className={`grid grid-cols-1 gap-6 items-start ${
          variant === 'drawer'
            ? ''
            : 'lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_380px]'
        }`}
      >
        <div className="w-full flex flex-col gap-5 p-4 sm:p-5 rounded-2xl border border-[#EBF4EA] bg-[#F5F5F5]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Channel <span className="text-red-500">*</span>
              </label>
              {!integration.isReady || channelOptions.length === 0 ? (
                <ChannelAvailabilityState
                  status={integration}
                  canOpenSettings={can(PERMISSIONS.SETTINGS.VIEW)}
                >
                  <p className="text-sm font-medium text-[#1F2241]">
                    This contact has no connected channel.
                  </p>
                </ChannelAvailabilityState>
              ) : (
                <CustomSelect
                  value={channel}
                  onChange={(val) => {
                    setChannel(val);
                    setSelectedTemplateId('');
                    setTemplateValues({});
                    setMediaFile(null);
                  }}
                  options={channelOptions}
                  disabled={isPending || channelOptions.length <= 1}
                  hideChevron={channelOptions.length <= 1}
                  className="w-full h-11"
                  buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4"
                  textSizeClass="text-sm"
                />
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Follow-up Type <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={followUpType}
                onChange={(val) => setFollowUpType(val)}
                options={[
                  { value: 'scheduled', label: 'Scheduled (Once)' },
                  { value: 'interval', label: 'Interval (Recurring)' },
                ]}
                disabled={isPending}
                className="w-full h-11"
                buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4"
                textSizeClass="text-sm"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Message Type <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={messageType}
                onChange={(val) => {
                  setMessageType(val);
                  setSelectedTemplateId('');
                  setTemplateValues({});
                  setMediaFile(null);
                }}
                options={[
                  { value: 'template', label: 'Template' },
                  { value: 'message', label: 'Message' },
                ]}
                disabled={isPending}
                className="w-full h-11"
                buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4"
                textSizeClass="text-sm"
              />
            </div>
          </div>

          {channel === 'email' && messageType === 'template' && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Email Template <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={selectedTemplateId}
                onChange={(val) => {
                  setSelectedTemplateId(val);
                  const t = emailTemplates.find(
                    (item) => String(item.id) === String(val),
                  );
                  if (t) {
                    if (t.subject) setEmailSubject(t.subject);
                    if (t.body) setStandardMessageBody(t.body);
                  }
                }}
                options={emailTemplates.map((t) => ({
                  value: String(t.id),
                  label: t.name || t.subject || 'Unnamed Template',
                }))}
                placeholder={
                  isEmailTemplatesLoading
                    ? 'Loading email templates...'
                    : 'Select an email template'
                }
                disabled={isPending || isEmailTemplatesLoading}
                className="w-full h-11"
                buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4"
                textSizeClass="text-sm"
              />
            </div>
          )}

          {channel === 'email' && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Email Subject
              </label>
              <input
                type="text"
                placeholder="e.g. Following up on our recent conversation"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
                disabled={isPending}
              />
            </div>
          )}

          {channel === 'whatsapp' && messageType === 'template' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Template <span className="text-red-500">*</span>
                </label>

                <CustomSelect
                  value={selectedTemplateId}
                  onChange={(val) => {
                    setSelectedTemplateId(val);
                    setTemplateValues({});
                    setMediaFile(null);
                  }}
                  options={whatsappTemplates.map((t) => ({
                    value: String(t.id || t.uuid),
                    label: t.name || 'Unnamed Template',
                  }))}
                  searchable
                  placeholder={
                    isWhatsappTemplatesLoading
                      ? 'Loading templates...'
                      : 'Select a template'
                  }
                  disabled={isPending || isWhatsappTemplatesLoading}
                  className="w-full h-11"
                  buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4"
                  textSizeClass="text-sm"
                />
              </div>

              {scheduledDateTimeField}

              {templateSchema && (
                <div className="flex flex-col gap-4 sm:col-span-2">
                  {templateSchema.media_field && (
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        {templateSchema.media_field.label}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-[#EBF4EA] bg-white hover:border-primary/40 transition-all">
                        <input
                          type="file"
                          accept={
                            templateSchema.media_field.type === 'image'
                              ? 'image/*'
                              : templateSchema.media_field.type === 'video'
                                ? 'video/*'
                                : '*/*'
                          }
                          onChange={(e) => setMediaFile(e.target.files[0])}
                          disabled={isPending}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        <div className="w-9 h-9 rounded-full bg-white border border-[#EBF4EA] flex items-center justify-center text-[#868686] shrink-0">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 16V4m0 0L7 9m5-5l5 5M5 20h14"
                            />
                          </svg>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-[#1F2241]">
                            Choose File
                          </span>
                          <span className="text-xs text-[#868686] truncate">
                            {mediaFile ? mediaFile.name : 'No file chosen'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {templateSchema.form_fields.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {templateSchema.form_fields.map((field) => (
                        <div key={field.id} className="flex flex-col gap-2">
                          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                            {field.label}{' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <VariableValueInput
                            id={`followup-var-${field.id}`}
                            value={templateValues[field.id] || ''}
                            disabled={isPending}
                            placeholder={`Enter value for ${field.label}`}
                            inputClassName="border-[#EBF4EA] text-[#1F2241] font-medium placeholder:text-[#868686]"
                            options={DYNAMIC_CONTACT_FIELDS.filter(
                              (contactField) => !contactField.windex,
                            ).map((contactField) => ({
                              value: contactField.key,
                              label: contactField.label,
                            }))}
                            onChange={(val) =>
                              setTemplateValues((prev) => ({
                                ...prev,
                                [field.id]: val,
                              }))
                            }
                            onPickField={(key) =>
                              setTemplateValues((prev) => ({
                                ...prev,
                                [field.id]: contactFieldToken(key),
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {channel === 'email' ? (
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                      Standard Message Body{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-400 font-medium">
                      {
                        (standardMessageBody || '').replace(/<[^>]*>/g, '')
                          .length
                      }
                      /5000
                    </span>
                  </div>

                  {/* ReactQuill Editor Wrapper */}
                  <div className="w-full rounded-2xl border border-[#EBF4EA] bg-white overflow-hidden shadow-xs focus-within:border-primary transition-all font-sans text-sm crm-quill-editor">
                    <style>{`
                      .crm-quill-editor .ql-toolbar.ql-snow {
                        border: none !important;
                        border-bottom: 1px solid #EBF4EA !important;
                        background-color: #FAFAFA;
                        padding: 8px 12px !important;
                      }
                      .crm-quill-editor .ql-container.ql-snow {
                        border: none !important;
                        font-family: inherit !important;
                        font-size: 0.875rem !important;
                      }
                      .crm-quill-editor .ql-editor {
                        min-height: 160px !important;
                        padding: 14px 16px !important;
                        font-family: inherit !important;
                        font-size: 0.875rem !important;
                        line-height: 1.5 !important;
                      }
                      .crm-quill-editor .ql-editor.ql-blank::before {
                        font-style: normal !important;
                        color: #9CA3AF !important;
                        left: 16px !important;
                        right: 16px !important;
                      }
                    `}</style>
                    <ReactQuill
                      theme="snow"
                      value={standardMessageBody}
                      onChange={setStandardMessageBody}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['link', 'image'],
                          ['clean'],
                        ],
                      }}
                      placeholder="Type your email body message here..."
                      className="text-sm font-normal text-gray-800"
                      disabled={isPending}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                    Standard Message Body{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={standardMessageBody}
                    onChange={(e) => setStandardMessageBody(e.target.value)}
                    placeholder="Type your message here..."
                    rows={3}
                    className="w-full p-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors resize-none"
                    disabled={isPending}
                  />
                </div>
              )}

              {scheduledDateTimeField}
            </div>
          )}

          {followUpType === 'interval' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Interval Value
                </label>
                <input
                  type="number"
                  min="1"
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(e.target.value)}
                  className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0"
                  disabled={isPending}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Interval Unit
                </label>
                <CustomSelect
                  value={intervalUnit}
                  onChange={(val) => setIntervalUnit(val)}
                  options={INTERVAL_UNITS}
                  disabled={isPending}
                  className="w-full h-11"
                  buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-all px-4"
                  textSizeClass="text-sm"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <DateTimePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  mode="datetime"
                  allowPastDates={false}
                  disablePastTime={true}
                  placeholderText="Select start date & time"
                  disabled={isPending}
                  className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  End Date
                </label>
                <DateTimePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  mode="datetime"
                  allowPastDates={false}
                  disablePastTime={true}
                  minDate={startDate}
                  placeholderText="Optional"
                  disabled={isPending}
                  className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Max Attempts
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)}
                  placeholder="0 for unlimited"
                  className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm font-medium focus:outline-none focus:border-primary transition-colors"
                  disabled={isPending}
                />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              checked={stopOnReply}
              onChange={(e) => setStopOnReply(e.target.checked)}
              disabled={isPending}
              className="peer sr-only"
            />
            <div
              className={`w-5 h-5 rounded-[6px] border flex items-center justify-center transition-all shrink-0 ${
                stopOnReply
                  ? 'bg-primary border-primary'
                  : 'border-[#868686] bg-transparent'
              }`}
            >
              {stopOnReply && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
            <span className="text-sm font-medium text-[#1F2241]">
              Stop follow-up on customer reply
            </span>
          </label>
        </div>

        {/* Right Column: Live Mockup Preview */}
        <div className="w-full flex flex-col items-center justify-center rounded-2xl border border-[#EBF4EA] bg-[#F5F5F5] p-5 sm:p-6 overflow-hidden">
          {channel === 'email' ? (
            <EmailTemplatePreview
              subject={previewEmailSubject}
              body={previewEmailBody}
              senderName={companyName}
              senderEmail={senderEmail}
              recipientEmail={contactEmail}
              headerTitle="Email Follow-Up Preview"
              showFooterBadge={true}
              footerBadgeText="Delivers directly to Gmail inbox in same thread"
              className="w-full"
            />
          ) : (
            <TemplatePhonePreview
              className="w-full"
              template={messageType === 'template' ? selectedTemplate : null}
              templateValues={templateValues}
              message={messageType === 'message' ? standardMessageBody : ''}
              mediaFile={mediaFile}
            />
          )}
        </div>
      </div>

      {/* Submit: bottom-most, always below both columns */}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 shrink-0 pt-2">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          variant="primary"
          className="h-10 px-6 text-sm font-semibold w-full sm:w-auto"
        >
          {isPending ? 'Saving...' : 'Add Follow-Up'}
          <span aria-hidden>→</span>
        </Button>
      </div>
    </div>
  );
}
